
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Card from '../../components/base/Card';
import Badge from '../../components/base/Badge';
import Button from '../../components/base/Button';
import { mockActivities } from '../../mocks/activities';

interface ActivitiesProps {
  openCreateModal?: boolean;
  onCloseCreateModal?: () => void;
}

export default function Activities({ openCreateModal = false, onCloseCreateModal }: ActivitiesProps) {
  const navigate = useNavigate();
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [selectedSpace, setSelectedSpace] = useState<string>('shenquan');

  useEffect(() => {
    if (openCreateModal) {
      setShowCreateModal(true);
    }
  }, [openCreateModal]);

  const handleCloseModal = () => {
    setShowCreateModal(false);
    if (onCloseCreateModal) {
      onCloseCreateModal();
    }
  };

  const formatCurrency = (amount: number) => {
    return `¥${(amount / 10000).toFixed(1)}万`;
  };

  const getStatusBadge = (status: string) => {
    const statusMap = {
      active: { variant: 'success' as const, label: '进行中' },
      planning: { variant: 'warning' as const, label: '筹备中' },
      completed: { variant: 'gray' as const, label: '已完成' },
      draft: { variant: 'info' as const, label: '草稿' }
    };
    return statusMap[status as keyof typeof statusMap] || { variant: 'gray' as const, label: '未知' };
  };

  const getTypeName = (type: string) => {
    const typeMap = {
      promotion: '美团平台大促',
      launch: '新品发布',
      member: '会员活动',
      seasonal: '节日营销'
    };
    return typeMap[type as keyof typeof typeMap] || type;
  };

  const filteredActivities = filterStatus === 'all' 
    ? mockActivities 
    : mockActivities.filter(activity => activity.status === filterStatus);

  const toggleFavorite = (activityId: string) => {
    setFavorites(prev => {
      const newSet = new Set(prev);
      if (newSet.has(activityId)) {
        newSet.delete(activityId);
      } else {
        newSet.add(activityId);
      }
      return newSet;
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
        {/* 页面标题和操作 */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">活动全流程管理</h1>
            <p className="text-gray-600">管理营销活动的完整生命周期</p>
          </div>
        </div>

        {/* 空间选择下拉框 */}
        <div className="mb-6">
          <div className="flex items-center">
            <div className="relative max-w-[200px]">
              <select
                value={selectedSpace}
                onChange={(e) => setSelectedSpace(e.target.value)}
                className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white"
              >
                <option value="shenquan">神券 X 会员营销活动</option>
                <option value="festival">神券节 & 节日营销 2025</option>
                <option value="new">新建空间</option>
              </select>
              <i className="ri-arrow-down-s-line absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
            </div>
          </div>
        </div>

        {/* 筛选条件 */}
        <Card className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex space-x-2">
              {[
                { key: 'all', label: '全部活动' },
                { key: 'active', label: '进行中' },
                { key: 'planning', label: '筹备中' },
                { key: 'completed', label: '已完成' },
                { key: 'draft', label: '草稿' }
              ].map((filter) => (
                <button
                  key={filter.key}
                  onClick={() => setFilterStatus(filter.key)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    filterStatus === filter.key
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
            <div className="flex items-center space-x-3">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                <input
                  type="text"
                  placeholder="搜索活动..."
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <Button onClick={() => setShowCreateModal(true)}>
                <i className="ri-add-line mr-2"></i>
                创建活动
              </Button>
            </div>
          </div>
        </Card>

        <div className="space-y-4">
          {filteredActivities.map((activity) => (
            <Card 
              key={activity.id} 
              className="hover:shadow-md transition-all duration-200"
            >
              <div className="flex items-start gap-6">
                {/* 左侧主要内容 */}
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 mr-4">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{activity.name}</h3>
                        <Badge {...getStatusBadge(activity.status)}>
                          {getStatusBadge(activity.status).label}
                        </Badge>
                        <Badge variant="info">{getTypeName(activity.type)}</Badge>
                        {/* 收藏按钮 */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavorite(activity.id);
                          }}
                          className="w-8 h-8 ml-2 hover:bg-gray-100 rounded-full transition-colors"
                          aria-label={favorites.has(activity.id) ? '取消收藏' : '收藏'}
                        >
                          <i 
                            className={`${favorites.has(activity.id) ? 'ri-star-fill' : 'ri-star-line'} text-lg transition-colors ${
                              favorites.has(activity.id) ? 'text-yellow-500' : 'text-gray-400 hover:text-yellow-500'
                            }`}
                          ></i>
                        </button>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        {activity.startDate} - {activity.endDate}
                      </p>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {activity.businessLines.map((line) => (
                          <span key={line} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                            {line}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-lg font-semibold text-gray-900">{formatCurrency(activity.budget)}预算</p>
                      {/* 人员头像 */}
                      <div className="flex items-center justify-end -space-x-2 mt-4">
                        <img 
                          src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=64&h=64&fit=crop&crop=faces&auto=format&q=80" 
                          alt="团队成员" 
                          className="w-8 h-8 rounded-full border-2 border-white object-cover"
                        />
                        <img 
                          src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=64&h=64&fit=crop&crop=faces&auto=format&q=80" 
                          alt="团队成员" 
                          className="w-8 h-8 rounded-full border-2 border-white object-cover"
                        />
                        <img 
                          src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=64&h=64&fit=crop&crop=faces&auto=format&q=80" 
                          alt="团队成员" 
                          className="w-8 h-8 rounded-full border-2 border-white object-cover"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* 右侧操作按钮 */}
                <div className="flex flex-col gap-3 pl-6 border-l border-gray-200 flex-shrink-0">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="whitespace-nowrap"
                  >
                    <i className="ri-pencil-line mr-2"></i>
                    编辑活动
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="whitespace-nowrap"
                  >
                    <i className="ri-file-upload-line mr-2"></i>
                    招商方案
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="whitespace-nowrap"
                    onClick={(e) => {
                      e?.stopPropagation();
                      navigate('/activity-assets');
                    }}
                  >
                    <i className="ri-folder-line mr-2"></i>
                    活动资产
                  </Button>
                </div>
              </div>
            </Card>
            ))}
        </div>

        {/* 创建活动模态框 */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">活动基本信息</h2>
                <button 
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
              
              <div className="space-y-5">
                {/* 项目名称 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <span className="text-red-500 mr-1">*</span>项目名称
                  </label>
                  <input 
                    type="text" 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="请输入"
                  />
                </div>

                {/* 运营主R */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <span className="text-red-500 mr-1">*</span>运营主R
                  </label>
                  <div className="relative">
                    <input 
                      type="text" 
                      className="w-full px-3 py-2 pr-20 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="请输入mis搜索"
                    />
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">5320739</span>
                  </div>
                </div>

                {/* 业务线 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <span className="text-red-500 mr-1">*</span>业务线
                  </label>
                  <div className="relative">
                    <select className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white">
                      <option value="">请选择</option>
                      <option>外卖业务线</option>
                      <option>到店业务线</option>
                      <option>到家业务线</option>
                      <option>酒店业务线</option>
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                  </div>
                </div>

                {/* 活动场景 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <span className="text-red-500 mr-1">*</span>活动场景
                    <i className="ri-question-line ml-1 text-gray-400 text-xs"></i>
                  </label>
                  <div className="relative">
                    <select className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white">
                      <option value="">请选择</option>
                      <option>神券节</option>
                      <option>外卖大促</option>
                      <option>新品发布</option>
                      <option>会员活动</option>
                      <option>节日营销</option>
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    如果是神券节请直接选择神券节,不要选"外卖大促"
                  </p>
                </div>

                {/* 项目范围 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <span className="text-red-500 mr-1">*</span>项目范围
                    <i className="ri-question-line ml-1 text-gray-400 text-xs"></i>
                  </label>
                  <div className="relative">
                    <input 
                      type="text" 
                      className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="请选择城市"
                    />
                    <button className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      <i className="ri-close-line"></i>
                    </button>
                  </div>
                </div>

                {/* 流量预估 */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <span className="text-red-500 mr-1">*</span>流量预估(日均pv/万)
                    </label>
                    <div className="relative">
                      <input 
                        type="number" 
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="0"
                      />
                      <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">万</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <span className="text-red-500 mr-1">*</span>流量预估(日均uv/万)
                    </label>
                    <div className="relative">
                      <input 
                        type="number" 
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="0"
                      />
                      <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">万</span>
                    </div>
                  </div>
                </div>

                {/* 活动总预算 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <span className="text-red-500 mr-1">*</span>活动总预算(元)
                  </label>
                  <div className="relative">
                    <input 
                      type="number" 
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0"
                    />
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">元</span>
                  </div>
                </div>

                {/* 关注人 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    关注人
                    <i className="ri-question-line ml-1 text-gray-400 text-xs"></i>
                  </label>
                  <div className="relative">
                    <input 
                      type="text" 
                      className="w-full px-3 py-2 pr-24 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="请输入mis搜索"
                    />
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">欧阳15220739</span>
                  </div>
                </div>

                {/* 项目时间 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <span className="text-red-500 mr-1">*</span>项目时间
                    <i className="ri-question-line ml-1 text-gray-400 text-xs"></i>
                  </label>
                  <div className="space-y-2">
                    <div className="relative">
                      <input 
                        type="text" 
                        className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="请选择时间范围"
                      />
                      <button className="absolute right-2 top-1/2 transform -translate-y-1/2 text-blue-600 hover:text-blue-700">
                        <i className="ri-add-line"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-8 pt-4 border-t border-gray-200">
                <Button variant="outline" onClick={handleCloseModal} className="px-6">
                  取消
                </Button>
                <Button className="px-6">
                  保存
                </Button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
